import {
  Area,
  Bar,
  CartesianGrid,
  ComposedChart,
  Label,
  LabelList,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { ChartTooltipContent } from "../ui/chart";
import { paletteColor, type FigurePalette } from "./mermaidSource";
import { xyChartDomain, xyChartRows, xySeriesKey, xySeriesName, type XyChartSpec } from "./xyChart";

/**
 * One ramp off the app accent, as the regulation dashboard charts use, so a
 * chart reads as part of the interface in every theme rather than a pasted
 * picture. Series beyond the ramp wrap around.
 */
const SERIES_COLORS: ReadonlyArray<string> = [
  "var(--color-primary)",
  "color-mix(in oklab, var(--color-primary) 55%, var(--color-card))",
  "var(--color-muted-foreground)",
  "color-mix(in oklab, var(--color-muted-foreground) 55%, var(--color-card))",
  "color-mix(in oklab, var(--color-primary) 30%, var(--color-card))",
];

const TICK_STYLE = { fill: "var(--color-muted-foreground)", fontSize: 11 } as const;
const AXIS_LABEL_STYLE = {
  fill: "var(--color-muted-foreground)",
  fontSize: 11,
  textAnchor: "middle",
} as const;

function seriesColor(index: number, palette: FigurePalette | null): string {
  return paletteColor(palette, index, SERIES_COLORS);
}

/**
 * Interactive rendering of a mermaid `xychart-beta` fence: hover tooltips,
 * a legend when there is more than one series, and the workspace's own
 * colours. Left-aligned and unframed, like a figure in a report.
 */
export function XyChartFigure({ spec }: { readonly spec: XyChartSpec }) {
  const rows = xyChartRows(spec);
  const domain = xyChartDomain(spec);
  const showLegend = spec.series.length > 1 || spec.series[0]?.title != null;
  const numberAxisProps = {
    type: "number" as const,
    domain,
    tickLine: false,
    axisLine: false,
    tick: TICK_STYLE,
  };
  const categoryAxisProps = {
    type: "category" as const,
    dataKey: "category",
    tickLine: false,
    axisLine: false,
    tick: TICK_STYLE,
    interval: 0 as const,
  };

  // No rotated axis labels: a sideways label pushes the plot right and looks
  // like wasted margin in a chat column. The vertical axis's label reads as a
  // caption under the title; the bottom axis keeps its label underneath.
  // In a horizontal chart the axes swap, so the labels swap with them.
  const captionLabel = spec.horizontal ? spec.xLabel : spec.yLabel;
  const bottomLabel = spec.horizontal ? spec.yLabel : spec.xLabel;

  return (
    <div className="chat-markdown-xychart" role="img" aria-label={spec.title ?? "Chart"}>
      {spec.title ? <div className="chat-markdown-xychart-title">{spec.title}</div> : null}
      {captionLabel ? <div className="chat-markdown-xychart-caption">{captionLabel}</div> : null}
      <div className="chat-markdown-xychart-plot">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={rows}
            layout={spec.horizontal ? "vertical" : "horizontal"}
            margin={{ top: 8, right: 12, bottom: bottomLabel ? 20 : 4, left: 0 }}
          >
            <CartesianGrid
              vertical={spec.horizontal}
              horizontal={!spec.horizontal}
              stroke="var(--color-border)"
              strokeOpacity={0.7}
            />
            <XAxis {...(spec.horizontal ? numberAxisProps : categoryAxisProps)} height={bottomLabel ? 40 : 28}>
              {bottomLabel ? (
                <Label value={bottomLabel} position="insideBottom" offset={-2} style={AXIS_LABEL_STYLE} />
              ) : null}
            </XAxis>
            {spec.horizontal ? (
              <YAxis {...categoryAxisProps} width="auto" />
            ) : (
              <YAxis {...numberAxisProps} width="auto" />
            )}
            <Tooltip
              wrapperStyle={{ zIndex: 40 }}
              itemSorter="dataKey"
              cursor={{ stroke: "var(--color-border)", fill: "var(--color-muted)", fillOpacity: 0.4 }}
              content={<ChartTooltipContent />}
            />
            {showLegend ? (
              <Legend
                verticalAlign="top"
                align="left"
                iconType="circle"
                iconSize={8}
                itemSorter={null}
                wrapperStyle={{ fontSize: 12, paddingBottom: 8 }}
              />
            ) : null}
            {spec.series.map((entry, index) => {
              const key = xySeriesKey(index);
              const name = xySeriesName(entry, index, spec.series.length);
              const color = seriesColor(index, spec.palette);
              if (entry.kind === "bar") {
                return (
                  <Bar
                    key={key}
                    dataKey={key}
                    name={name}
                    fill={color}
                    {...(spec.stacked ? { stackId: "stack" } : {})}
                    radius={spec.stacked ? 0 : spec.horizontal ? [0, 3, 3, 0] : [3, 3, 0, 0]}
                    maxBarSize={48}
                    isAnimationActive={false}
                  >
                    {spec.showDataLabel ? (
                      <LabelList
                        dataKey={key}
                        position={spec.horizontal ? "right" : "top"}
                        style={TICK_STYLE}
                      />
                    ) : null}
                  </Bar>
                );
              }
              if (entry.kind === "line") {
                return (
                  <Line
                    key={key}
                    type="monotone"
                    dataKey={key}
                    name={name}
                    stroke={color}
                    strokeWidth={2}
                    dot={{ r: 3, strokeWidth: 0, fill: color }}
                    activeDot={{ r: 5 }}
                    isAnimationActive={false}
                  />
                );
              }
              return (
                <Area
                  key={key}
                  type="monotone"
                  dataKey={key}
                  name={name}
                  {...(spec.stacked ? { stackId: "stack" } : {})}
                  stroke={color}
                  fill={color}
                  fillOpacity={spec.stacked ? 0.35 : 0.16}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 5 }}
                  isAnimationActive={false}
                />
              );
            })}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
